/**
 * The Summary Reports grid: columns, rows, fetching, aggregation and the chart pane.
 *
 * The apiRef is MUI's handle on the grid. The View owns it and uses it to export and
 * restore saved reports.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { useSelector } from 'react-redux';
import { Box, Fade, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import {
    DataGridPremium,
    type DataGridPremiumProps,
    type GridPivotModel,
    GridChartsIntegrationContextProvider,
    GridChartsPanel,
    GridChartsRendererProxy,
    useGridApiRef,
    useGridChartsIntegrationContext,
} from '@mui/x-data-grid-premium';
import { ChartsRenderer, configurationOptions } from '@mui/x-charts-premium/ChartsRenderer';
import { legendClasses } from '@mui/x-charts/ChartsLegend';

import { fetchAllFeaturesByWhereClause } from '../../../../services/FeatureServiceGridDataSource.ts';
import { buildSummaryReportColumns } from '../../utils/summaryReportColumns.ts';
import { getEsriFieldsFromUrl } from '../../../../utils/MapUtilities.tsx';
import { getAppConfig, getViewServiceUrl } from '../../../../utils/ReduxHelper.jsx';
import {
    summariesViewFields,
    summariesAggregationModel,
    summariesAggregationFunctions,
    summariesOutFields,
} from '../../config/SummaryReportsConfig.tsx';
import type { RootState } from '../../../../redux/store.tsx';
import type {
    SummaryReportChartState,
    SummaryReportGridState,
    SummaryReportPivotState,
} from '../../types.ts';
import {
    getCategoryLimit,
    getCardinalityOverrideKey,
    getCategoryCount,
    describeChart,
    exceedsCategoryLimit,
    hasConfiguredChart,
} from '../../utils/chartState.ts';
import { fillPaneSizing } from '../../utils/chartSizing.ts';
import ChartCardinalityNotice from '../ChartCardinalityNotice/ChartCardinalityNotice.tsx';
import ChartSkeleton from '../ChartSkeleton/ChartSkeleton.tsx';
import ChartBelowFoldIndicator from '../ChartBelowFoldIndicator/ChartBelowFoldIndicator.tsx';
import { withBlankGroupingKeys } from '../../utils/blankColGrouping.ts';
import {
    applyChartState,
    CHART_FIELD_WAIT_MS,
    getMissingChartFields,
    type ChartContextLookup,
} from '../../utils/chartPersistence.ts';
import { EMPTY_PIVOT_MODEL, isPivotConfigured } from '../../utils/pivotState.ts';
import { scrollToTopWithinScroller } from '../../utils/scrolling.ts';
import { useChartDimensionGrouping } from '../../hooks/useChartDimensionGrouping.ts';
import { useIsInView } from '../../hooks/useIsInView.ts';
import {
    CHART_PANE_TRANSITION_MS,
    DEFAULT_LAYOUT,
    LAYOUT_SX,
    type SummaryReportLayout,
} from '../../utils/layout.ts';

// ─── Stable prop identities ──────────────────────────────────────────────────
// Hoisted out of render. The grid re-reads these every pass, and an inline literal costs a
// row-id recomputation or a slot re-evaluation even when nothing changed.

/** The id this grid registers its chart under. */
const CHART_ID = 'main';

const getRowId = (row: any) => row.OBJECTID;

const PAGE_SIZE_OPTIONS = [100, 250, 500, 1000];

const GRID_INITIAL_STATE = {
    pagination: { paginationModel: { pageSize: 250 } },
    aggregation: { model: summariesAggregationModel },
};

const GRID_EXPERIMENTAL_FEATURES = { charts: true };

const GRID_SLOTS = { chartsPanel: GridChartsPanel };

const GRID_SLOT_PROPS = { chartsPanel: { schema: configurationOptions } };

const CHARTS_SIDEBAR_VALUE = 'charts';

// ─── Chart pane styling ──────────────────────────────────────────────────────

const chartSurfaceSx = {
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'background.paper',
    overflow: 'hidden',
};

const chartHeaderSx = {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: 2.25,
    padding: '12px 20px !important',
    borderBottom: '1px solid',
    borderColor: 'divider',
};

const chartTitleSx = { flex: 1, minWidth: 0, fontSize: '1rem', fontStyle: 'italic' };

/** The title is also the rename target, so hovering has to say so. */
const editableChartTitleSx = {
    ...chartTitleSx,
    cursor: 'text',
    borderRadius: 1,
    px: 0.75,
    mx: -0.75,
    '&:hover': { backgroundColor: 'action.hover' },
};

/** Matches the Typography it replaces, so opening the editor doesn't shift the header. */
const chartTitleInputSx = {
    flex: 1,
    minWidth: 0,
    '& .MuiInputBase-input': { fontSize: '1rem', fontStyle: 'italic', py: 0.25 },
};

const chartWhitespace = { width: 0, height: 0, lineHeight: 0.5 };

/** The legend as a card rather than loose text. By selector, since we don't render it. */
const chartLegendCardSx = {
    [`& .${legendClasses.root}`]: {
        boxSizing: 'border-box',
        maxWidth: '100%',
        px: 1.5,
        py: 1.25,
        m: 1,
        columnGap: 2,
        rowGap: 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        backgroundColor: 'background.paper',
        boxShadow: 1,
    },
};

/** The plot's box, inset so axis labels and legend clear the border and the header rule. */
const chartBodySx = {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    p: 3,
    ...chartLegendCardSx,
};

const chartSkeletonOverlaySx = {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    pointerEvents: 'none',
    backgroundColor: 'background.paper',
};

/** Fills the body, giving the chart a container to measure. See utils/chartSizing. */
const chartProxyFillSx = {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
};

const hiddenChartProxySx = {
    ...chartProxyFillSx,
    opacity: 0,
    pointerEvents: 'none',
    transition: `opacity ${CHART_PANE_TRANSITION_MS}ms ease`,
};

/** Focusable only as a scroll target, so it shouldn't look like a tab stop. */
const paneFocusSx = { '&:focus': { outline: 'none' } };

const visibleChartProxySx = {
    ...chartProxyFillSx,
    padding: '40px!important',
    opacity: 1,
    transition: `opacity ${CHART_PANE_TRANSITION_MS}ms ease`,
};

// ─── Chart pane ──────────────────────────────────────────────────────────────

function ChartTitleInput({
    title,
    placeholder,
    onCommit,
    onCancel,
}: {
    title: string;
    placeholder: string;
    onCommit: (title: string) => void;
    onCancel: () => void;
}) {
    const [draft, setDraft] = useState(title);

    return (
        <TextField
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => onCommit(draft)}
            onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === 'Enter') {
                    event.preventDefault();
                    onCommit(draft);
                } else if (event.key === 'Escape') {
                    onCancel();
                }
            }}
            placeholder={placeholder}
            autoFocus
            size='small'
            variant='standard'
            sx={chartTitleInputSx}
            slotProps={{ htmlInput: { 'aria-label': 'chart name' } }}
        />
    );
}

/**
 * The chart pane's box.
 *
 * Split out because GridChartsRendererProxy renders no DOM of its own, and reading the
 * integration's context means being inside its provider.
 */
function ChartPane({
    apiRef,
    layout,
    showSkeleton,
    onDismissSkeleton,
    onChartVisibleChange,
    title,
    onTitleChange,
}: {
    apiRef: ReturnType<typeof useGridApiRef>;
    layout: SummaryReportLayout;
    showSkeleton: boolean;
    onDismissSkeleton: () => void;
    onChartVisibleChange?: (visible: boolean) => void;
    /** The name the user typed, or undefined while the chart goes by its description. */
    title?: string;
    onTitleChange?: (chartId: string, title: string) => void;
}) {
    const { chartStateLookup } = useGridChartsIntegrationContext();
    const chartState = chartStateLookup?.[CHART_ID];
    const hasChart = hasConfiguredChart(chartState);
    const layoutSx = LAYOUT_SX[layout];
    const paneRef = useRef<HTMLDivElement | null>(null);

    // How many dimensions this chart type accepts — a pie takes one. The integration copies
    // this onto chartState from the same schema we hand the panel, so read the source.
    const typeSchema = configurationOptions[chartState?.type ?? ''];

    // Without this the chart plots one point per record. See the hook.
    useChartDimensionGrouping(apiRef, CHART_ID, typeSchema?.maxDimensions);

    // ── Cardinality guard ──
    // Not how many dimensions the chart takes, but how many categories one dimension opens
    // into. Grouping collapses a dimension to its distinct values, but some fields are
    // distinct per record anyway and others swing with the filter. Past the ceiling we show
    // the count and let the user insist. Ceilings live in utils/chartState.
    const categoryCount = getCategoryCount(chartState);
    const overLimit = exceedsCategoryLimit(chartState);
    const showChartSkeleton = showSkeleton && !hasChart;
    const paneOpen = hasChart || showChartSkeleton;

    // The arrangement toggle lives outside this provider and has nothing to arrange until
    // the pane is up. Reported rather than derived there, since `hasChart` needs the
    // integration's context.
    useEffect(() => {
        onChartVisibleChange?.(paneOpen);
    }, [paneOpen, onChartVisibleChange]);

    // Pinned to the selection it was given for, so a yes for one field at one size never
    // carries over to a bigger one.
    const overrideKey = getCardinalityOverrideKey(chartState);
    const [allowedKey, setAllowedKey] = useState<string | null>(null);
    const blockedForSize = overLimit && allowedKey !== overrideKey;
    const allowThisChart = useCallback(() => setAllowedKey(overrideKey), [overrideKey]);

    const notice = useMemo(
        () => (
            <ChartCardinalityNotice
                fieldLabel={chartState?.dimensions?.[0]?.label ?? 'This field'}
                categoryCount={categoryCount}
                limit={getCategoryLimit(chartState?.type)}
                chartTypeLabel={typeSchema?.label}
                onOverride={allowThisChart}
            />
        ),
        [chartState?.dimensions, chartState?.type, categoryCount, typeSchema, allowThisChart]
    );

    // Only stacked can hide the pane; side-by-side keeps it beside the grid.
    const paneInView = useIsInView(paneRef, { enabled: layout === 'stacked' && paneOpen });
    const showBelowFoldCue = layout === 'stacked' && paneOpen && !paneInView;
    const scrollToChart = useCallback(() => {
        const pane = paneRef.current;
        if (!pane) return;

        scrollToTopWithinScroller(pane);
        pane.focus?.({ preventScroll: true });
    }, []);

    const dismissChart = useCallback(() => {
        onDismissSkeleton();
        onTitleChange?.(CHART_ID, '');
        apiRef.current?.updateChartDimensionsData(CHART_ID, []);
        apiRef.current?.updateChartValuesData(CHART_ID, []);
    }, [apiRef, onDismissSkeleton, onTitleChange]);

    // Swapping the node through `onRender` rather than unmounting the proxy, which would
    // deregister the chart and its configuration. The same hook is where the renderer's
    // default sizing comes off; see utils/chartSizing.
    const renderChart = useCallback(
        (type: string, props: Record<string, any>, Component: React.ComponentType<any>) => {
            if (blockedForSize) return notice;

            // Only worth sizing while there is a pane: collapsed it measures zero, and a
            // chart with no height of its own warns about the container it was given.
            const sized = hasChart ? fillPaneSizing(type, props, chartState?.configuration) : props;

            return <Component {...sized} skipAnimation />;
        },
        [blockedForSize, notice, hasChart, chartState?.configuration]
    );

    // What the chart plots, unless the user named it. A typed name survives a change of
    // fields until they clear it, which hands the description back.
    const generatedTitle = describeChart(chartState) ?? 'New chart';
    const chartTitle = title?.trim() || generatedTitle;

    const [renaming, setRenaming] = useState(false);
    const commitTitle = useCallback(
        (next: string) => {
            setRenaming(false);
            onTitleChange?.(CHART_ID, next);
        },
        [onTitleChange]
    );
    const cancelRename = useCallback(() => setRenaming(false), []);

    // A pane closed mid-rename shouldn't come back still editing.
    useEffect(() => {
        if (!paneOpen) setRenaming(false);
    }, [paneOpen]);

    return (
        <>
            {/* Ahead of the pane, so its zero-height box lands on the grid's bottom edge,
                which is what the cue hangs off. */}
            {layout === 'stacked' && (
                <ChartBelowFoldIndicator open={showBelowFoldCue} onClick={scrollToChart} />
            )}

            <Box
                ref={paneRef}
                // Focusable as a scroll target, never a tab stop.
                tabIndex={-1}
                sx={[paneOpen ? layoutSx.chartOpen : layoutSx.chartCollapsed, paneFocusSx]}
                style={{ position: `${paneOpen ? 'relative' : 'absolute'}` }}
            >
                <Box sx={chartSurfaceSx}>
                    {paneOpen && (
                        <Box sx={chartHeaderSx}>
                            <InsertChartOutlinedIcon fontSize='large' color='action' />

                            {renaming ? (
                                <ChartTitleInput
                                    title={title ?? ''}
                                    placeholder={generatedTitle}
                                    onCommit={commitTitle}
                                    onCancel={cancelRename}
                                />
                            ) : (
                                <>
                                    <Typography
                                        variant='subtitle2'
                                        noWrap
                                        title={chartTitle}
                                        onClick={() => setRenaming(true)}
                                        sx={editableChartTitleSx}
                                    >
                                        {chartTitle}
                                    </Typography>

                                    <Tooltip title='Rename chart'>
                                        <IconButton
                                            size='small'
                                            aria-label='rename chart'
                                            onClick={() => setRenaming(true)}
                                        >
                                            <EditOutlinedIcon fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}

                            <Tooltip title='Dismiss chart'>
                                <IconButton
                                    size='small'
                                    aria-label='dismiss chart'
                                    onClick={dismissChart}
                                >
                                    <CloseIcon fontSize='small' />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                    <Box sx={chartBodySx}>
                        {/* Crossfades with the chart underneath rather than cutting. */}
                        <Fade in={showChartSkeleton} unmountOnExit>
                            <Box sx={chartSkeletonOverlaySx}>
                                <ChartSkeleton />
                            </Box>
                        </Fade>

                        <Box sx={showChartSkeleton ? hiddenChartProxySx : visibleChartProxySx}>
                            <GridChartsRendererProxy
                                id={CHART_ID}
                                renderer={ChartsRenderer}
                                onRender={renderChart}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
            {layout === 'stacked' && <Box sx={chartWhitespace}>&nbsp;</Box>}
        </>
    );
}

// ─── Chart persistence ───────────────────────────────────────────────────────

/** A chart waiting to be restored, and the pivot mode it belongs to. */
interface ChartRestoreRequest {
    /** The report's chart. Undefined for a report with none, which clears the pane. */
    chartState?: SummaryReportChartState;
    /** Whether the report's pivot is on. The restore waits for the grid to agree. */
    pivotActive: boolean;
}

/**
 * Saves and restores the chart from inside the charts context.
 *
 * Renders nothing: it exists to sit inside the provider, the only place the chart's type and
 * configuration can be read or written. See utils/chartPersistence.
 */
function ChartStatePersistence({
    apiRef,
    contextRef,
    request,
    pivotActive,
    onRestored,
}: {
    apiRef: ReturnType<typeof useGridApiRef>;
    contextRef?: RefObject<ChartContextLookup>;
    request: ChartRestoreRequest | null;
    /** The grid's pivot mode, not ours. The restore waits on what the grid has done. */
    pivotActive: boolean;
    onRestored: () => void;
}) {
    const { chartStateLookup, setChartState } = useGridChartsIntegrationContext();

    // Keeps the View's ref current, so a save can reach this half of the chart state.
    useEffect(() => {
        if (contextRef) contextRef.current = chartStateLookup;
    }, [chartStateLookup, contextRef]);

    useEffect(() => {
        if (!request || !apiRef.current) return undefined;

        // Wait for the grid's pivot mode to match the report's. A chart built while pivoted
        // names generated columns, and the integration rewrites the selection on every
        // `pivotModeChange` — restoring on the wrong side of that hands it to the pruner.
        if (request.pivotActive !== pivotActive) return undefined;

        let settled = false;

        // `force` is the deadline: apply what's there and drop the rest. Until then a
        // missing field is treated as one the grid hasn't built yet.
        const attempt = (force: boolean) => {
            const api = apiRef.current;
            if (settled || !api) return;
            if (!force && getMissingChartFields(api, request.chartState).length > 0) return;

            settled = true;
            const dropped = applyChartState(
                api,
                setChartState,
                request.chartState,
                Object.keys(api.state.chartsIntegration?.charts ?? {})
            );
            if (dropped.length > 0) {
                console.warn(
                    'SummaryReportsGrid: dropped chart fields the loaded data no longer has',
                    dropped
                );
            }
            onRestored();
        };

        attempt(false);
        if (settled) return undefined;

        const unsubscribe = apiRef.current.subscribeEvent('columnsChange', () => attempt(false));
        const deadline = setTimeout(() => attempt(true), CHART_FIELD_WAIT_MS);
        return () => {
            settled = true;
            unsubscribe();
            clearTimeout(deadline);
        };
    }, [request, pivotActive, apiRef, setChartState, onRestored]);

    return null;
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface SummaryReportGridProps {
    /** Created by the View, which uses it to export and restore report state. */
    apiRef: ReturnType<typeof useGridApiRef>;
    /** Grid state to restore on mount, from a loaded report. */
    initialGridState?: SummaryReportGridState;
    /** Carried beside `initialGridState`, which never includes it. See utils/pivotState. */
    initialPivotState?: SummaryReportPivotState;
    /**
     * Carried beside `initialGridState` too, for a different reason: pivoting's export
     * processor overwrites what the integration exports. See utils/chartPersistence.
     */
    initialChartState?: SummaryReportChartState;
    /** Names the user typed, per chart id. Owned by the View, which saves them. */
    chartTitles?: Record<string, string>;
    /** A chart was renamed. A blank name asks for the generated description back. */
    onChartTitleChange?: (chartId: string, title: string) => void;
    /**
     * Filled with the chart's type and configuration, which the View needs for a save and
     * can't read from outside the provider this grid renders.
     */
    chartContextRef?: RefObject<ChartContextLookup>;
    /** Reports the row count whenever the rows change. */
    onRowCountChange?: (count: number) => void;
    /** Reports whether the grid is still fetching. */
    onLoadingChange?: (isLoading: boolean) => void;
    /** Reports whether a chart pane is on screen. */
    onChartVisibleChange?: (visible: boolean) => void;
    /** How the grid and chart are arranged. */
    layout?: SummaryReportLayout;
}

// ─── Component ───────────────────────────────────────────────────────────────

function SummaryReportsGrid({
    apiRef,
    initialGridState,
    initialPivotState,
    initialChartState,
    chartTitles,
    onChartTitleChange,
    chartContextRef,
    onRowCountChange,
    onLoadingChange,
    onChartVisibleChange,
    layout = DEFAULT_LAYOUT,
}: SummaryReportGridProps) {
    const layoutSx = LAYOUT_SX[layout];

    const [rows, setRows] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showChartSkeleton, setShowChartSkeleton] = useState(false);
    const gridColumns = useRef<NonNullable<DataGridPremiumProps['columns']>>([]);

    const definitionExpression = useSelector(
        (state: RootState) => state.filter.definitionExpression
    );

    // Which filter the rows belong to, and which state we've already applied. Together they
    // stop a report's grid state landing on the previous filter's rows.
    const loadedExpressionRef = useRef<string | undefined>(undefined);
    const restoredStateRef = useRef<SummaryReportGridState | undefined>(undefined);

    // Pivot is held here rather than left to the grid: `exportState()` doesn't carry it and
    // `restoreState()` won't apply it, so the controlled props are the only way in.
    const [pivotModel, setPivotModel] = useState<GridPivotModel>(EMPTY_PIVOT_MODEL);
    const [pivotActive, setPivotActive] = useState(false);

    // What the grid actually has, which trails what we ask for by a render pass. The restore
    // below waits on this rather than on `pivotActive`, since only this one is in step.
    const [gridPivotActive, setGridPivotActive] = useState(false);

    // The chart half of a load, queued below and carried out by ChartStatePersistence once
    // the pivot settles. Null when nothing is pending.
    const [chartRestore, setChartRestore] = useState<ChartRestoreRequest | null>(null);
    const clearChartRestore = useCallback(() => setChartRestore(null), []);

    useEffect(() => {
        if (!apiRef.current) return undefined;

        const syncPivotMode = () =>
            setGridPivotActive(Boolean(apiRef.current?.state?.pivoting?.active));
        syncPivotMode();

        const unsubscribe = apiRef.current.subscribeEvent('pivotModeChange', syncPivotMode);
        return () => {
            unsubscribe();
        };
    }, [apiRef]);

    // Reload whenever the filter changes.
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                const viewUrl = getViewServiceUrl();
                if (!viewUrl) return;

                const { env, summaryReportsView } = getAppConfig();
                const reportsViewUrl = `${viewUrl}/${summaryReportsView[env].layerId}`;
                const esriFields = await getEsriFieldsFromUrl(reportsViewUrl);
                // Blanks would otherwise group at the root, one loose row each, and reach
                // the chart as a slice per record. See utils/blankColGrouping.
                const colDefs = withBlankGroupingKeys(
                    buildSummaryReportColumns(esriFields, summariesViewFields)
                );

                gridColumns.current = colDefs;

                const allRows = await fetchAllFeaturesByWhereClause(
                    reportsViewUrl,
                    definitionExpression || '1=1',
                    'OBJECTID',
                    summariesOutFields
                );

                // Deduplicate by OBJECTID.
                const seenIds: Set<any> = new Set();
                const cleanedRows = allRows.filter((f) => {
                    if (!seenIds.has(f.OBJECTID)) {
                        seenIds.add(f.OBJECTID);
                        return true;
                    }
                    return false;
                });
                setRows(cleanedRows);
                loadedExpressionRef.current = definitionExpression;
            } catch (error) {
                console.error('Error loading summary report data:', error);
                setRows([]);
                loadedExpressionRef.current = definitionExpression;
            } finally {
                setIsLoading(false);
            }
        };

        const config = getAppConfig();
        if (config) {
            loadData();
        }
    }, [definitionExpression]);

    // Report the row count upward on every change.
    useEffect(() => {
        if (!apiRef.current || !onRowCountChange) return undefined;

        const emitRowCount = () => onRowCountChange(apiRef.current?.getRowsCount() ?? 0);
        emitRowCount();

        const unsubscribe = apiRef.current.subscribeEvent('rowsSet', emitRowCount);
        return () => {
            unsubscribe();
        };
    }, [apiRef, onRowCountChange]);

    // Mirror the loading state upward.
    useEffect(() => {
        onLoadingChange?.(isLoading);
    }, [isLoading, onLoadingChange]);

    // Opening the charts sidebar means a chart is coming, which is when the placeholder
    // earns its place — well before any field is picked.
    const handleSidebarOpen = useCallback((params: { value?: string }) => {
        if (params?.value === CHARTS_SIDEBAR_VALUE) {
            setShowChartSkeleton(true);
        }
    }, []);

    const hideChartSkeleton = useCallback(() => {
        setShowChartSkeleton(false);
    }, []);

    // Restores a loaded report onto the grid, in a specific order.
    //
    // Wait for the rows to match the current filter. A load swaps the filter and hands us
    // new grid state in the same pass, so restoring on `!isLoading` alone lands on the
    // previous filter's rows and has to run again when the real ones arrive.
    //
    // Then take the pivot off. Switching it off makes the grid restore its own pre-pivot
    // snapshot from a layout effect, which would land on top of ours. Waiting on the grid's
    // pivot mode rather than our own puts this effect after that restore, not before it.
    // SummaryReportsGrid.pivot.spec covers the ordering.
    useEffect(() => {
        if (!initialGridState || !apiRef.current || isLoading) return;
        if (loadedExpressionRef.current !== definitionExpression) return;
        if (restoredStateRef.current === initialGridState) return;

        if (gridPivotActive) {
            setPivotActive(false);
            return;
        }

        restoredStateRef.current = initialGridState;
        try {
            apiRef.current.restoreState(initialGridState);
        } catch (e) {
            console.warn('SummaryReportsGrid: Failed to restore grid state', e);
        }

        // Model before mode: activating against an empty model flashes the empty-pivot
        // overlay. A report without a pivot clears it explicitly, or the last one lingers.
        const restoring = isPivotConfigured(initialPivotState);
        setPivotModel(restoring ? (initialPivotState!.model as GridPivotModel) : EMPTY_PIVOT_MODEL);
        setPivotActive(restoring);

        // The chart comes last and not from here: its fields name the columns the pivot mode
        // above produces. Queued even when there is no chart, so a chartless report takes
        // down whatever is on screen.
        setChartRestore({ chartState: initialChartState, pivotActive: restoring });
    }, [
        initialGridState,
        initialPivotState,
        initialChartState,
        isLoading,
        definitionExpression,
        gridPivotActive,
        apiRef,
    ]);

    return (
        <Box sx={layoutSx.container}>
            <GridChartsIntegrationContextProvider>
                <DataGridPremium
                    apiRef={apiRef}
                    columns={gridColumns.current}
                    rows={rows}
                    getRowId={getRowId}
                    loading={isLoading}
                    aggregationFunctions={summariesAggregationFunctions}
                    pagination
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    initialState={GRID_INITIAL_STATE}
                    showToolbar
                    chartsIntegration
                    experimentalFeatures={GRID_EXPERIMENTAL_FEATURES}
                    slots={GRID_SLOTS}
                    slotProps={GRID_SLOT_PROPS}
                    onSidebarOpen={handleSidebarOpen}
                    onSidebarClose={hideChartSkeleton}
                    pivotModel={pivotModel}
                    onPivotModelChange={setPivotModel}
                    pivotActive={pivotActive}
                    onPivotActiveChange={setPivotActive}
                    sx={layoutSx.grid}
                />
                <ChartStatePersistence
                    apiRef={apiRef}
                    contextRef={chartContextRef}
                    request={chartRestore}
                    pivotActive={gridPivotActive}
                    onRestored={clearChartRestore}
                />
                <ChartPane
                    apiRef={apiRef}
                    layout={layout}
                    showSkeleton={showChartSkeleton}
                    onDismissSkeleton={hideChartSkeleton}
                    onChartVisibleChange={onChartVisibleChange}
                    title={chartTitles?.[CHART_ID]}
                    onTitleChange={onChartTitleChange}
                />
            </GridChartsIntegrationContextProvider>
        </Box>
    );
}

export default memo(SummaryReportsGrid);
